import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Scale, ShieldCheck, Check, Loader2, Building2, ArrowUpRight } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import { usePreferences } from '../../contexts/PreferencesContext';
import { PracticeArea, Role, ROLE_LABELS, rolesOfUser } from '../../types/practiceArea';
import { launchCorporateSuite } from '../../services/corporateApi';
import TrialGate, { ensureTrialOnServer } from '../onboarding/TrialGate';
import { auth } from '../../utils/firebase';
import { enterRole, readLastRole, rememberLastRole, CHOSEN_KEY } from '../../utils/activeRole';
import { cn } from '../../lib/utils';

/**
 * Per-login workspace chooser (role-scoped workspaces).
 *
 * Shown once per browser session, AFTER login, BEFORE the dashboard — but ONLY
 * when the lawyer has more than one IN-APP role (see PracticeAreaGate in
 * App.tsx). It renders one card PER ROLE the user is actually registered for
 * (never the old fixed 3-card set), so a criminal-only lawyer never sees a
 * civil card and vice-versa — and a 0-role user is never trapped behind an
 * empty, unsatisfiable chooser.
 *
 * Selecting a criminal/civil card writes the active ROLE to sessionStorage
 * (`nyayabodh_active_role`, sent as X-Active-Role by the axios interceptor) AND
 * sets a representative SESSION LENS for backward-compat with the existing
 * lens-based sidebar gating. Corporate is a SEPARATE app (corporate-web) reached
 * via SSO — its card hands off through launchCorporateSuite() rather than
 * setting a lens.
 *
 * Gating uses sessionStorage so a fresh OAuth session re-prompts (the
 * "every login" requirement), while a reload within the same session does not.
 *
 * QA Q28: sessionStorage is per TAB, so the same login re-prompted in every
 * new tab. The "every login" requirement is kept exactly — a new sign-in
 * still prompts — but a new tab in the SAME sign-in (same Firebase
 * `lastSignInTime`) restores the role last chosen in it
 * (restoreSameSignInRole), and the chooser preselects the last choice.
 * Single-role lawyers never see the chooser at all (PracticeAreaGate).
 */

// E2E escape hatch: automated suites set this localStorage marker (seeded by
// Playwright global-setup into the saved session) so the per-login chooser
// doesn't block tests that deep-link to routes. Real users never have it —
// sessionStorage isn't persisted by Playwright storageState, so a localStorage
// marker is the only reliable seed point. NOT a security control.
const E2E_BYPASS_KEY = 'nyayabodh.e2e';

export function hasChosenPracticeAreaThisSession(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (localStorage.getItem(E2E_BYPASS_KEY) === '1') return true;
    return sessionStorage.getItem(CHOSEN_KEY) === '1';
  } catch {
    return true; // storage blocked → don't trap the user behind the chooser
  }
}

/** The current Firebase sign-in's identity stamp, or null if unknown. */
export function currentSignIn(): string | null {
  try {
    return auth.currentUser?.metadata?.lastSignInTime ?? null;
  } catch {
    return null;
  }
}

/** True when the last role was chosen in THIS sign-in and is still one of the
 *  lawyer's in-app roles — i.e. a new tab of the same login, not a new login.
 *  Pure check; restoreSameSignInRole performs the write. */
export function canRestoreSameSignInRole(inAppRoles: readonly Role[]): boolean {
  const last = readLastRole();
  const signIn = currentSignIn();
  return !!(last && last.signIn && signIn && last.signIn === signIn && inAppRoles.includes(last.role));
}

export function restoreSameSignInRole(inAppRoles: readonly Role[]): Role | null {
  if (!canRestoreSameSignInRole(inAppRoles)) return null;
  const last = readLastRole()!;
  enterRole(last.role);
  return last.role;
}

// In-app roles the chooser can render as a session-lens card. `corporate` is
// intentionally excluded here — it is a separate app handled by its own launch
// card.
type InAppRole = Extract<Role, 'criminal' | 'civil'>;

interface RoleCard {
  role: InAppRole;
  // Representative lens for backward-compat with the lens-based sidebar gating.
  lens: PracticeArea;
  title: string;
  subtitle: string;
  description: string;
  icon: LucideIcon;
  iconBg: string;
  selectedBorder: string;
  selectedBg: string;
}

// role → display metadata. Styling reused verbatim from the previous fixed
// criminal / civil_consumer cards.
const ROLE_CARDS: Record<InAppRole, RoleCard> = {
  criminal: {
    role: 'criminal',
    lens: 'criminal',
    title: ROLE_LABELS.criminal,
    subtitle: 'FIR · Bail · Case Theory',
    description: 'BNS/BNSS/BSA research, FIR analysis, bail strategy, and the 3-stage case theory engine.',
    icon: Scale,
    iconBg: 'bg-legal-green-100 text-legal-green-700 dark:bg-legal-green-900/30 dark:text-legal-green-300',
    selectedBorder: 'border-legal-green-500',
    selectedBg: 'bg-legal-green-50/60 dark:bg-legal-green-900/15',
  },
  civil: {
    role: 'civil',
    lens: 'civil_consumer',
    title: ROLE_LABELS.civil,
    subtitle: 'Notice · Forum · Limitation',
    description: 'Legal-notice analysis, the forum & limitation calculator, and consumer/chit-fund/arbitration research.',
    icon: ShieldCheck,
    iconBg: 'bg-gold-100 text-gold-700 dark:bg-gold-900/30 dark:text-gold-400',
    selectedBorder: 'border-gold-500',
    selectedBg: 'bg-gold-50/60 dark:bg-gold-900/15',
  },
};

// Deterministic render order (criminal first, then civil).
const IN_APP_ROLE_ORDER: InAppRole[] = ['criminal', 'civil'];

interface Props {
  onDone: () => void;
}

const PracticeAreaChooser: React.FC<Props> = ({ onDone }) => {
  const profile = useSelector((s: RootState) => s.auth.user);
  const { setActivePracticeArea } = usePreferences();
  const roles = rolesOfUser(profile?.practice_areas);
  // Preselect the last workspace chosen (any sign-in) — one click to continue.
  const [selected, setSelected] = useState<InAppRole | undefined>(() => {
    const last = readLastRole()?.role;
    return last && roles.has(last) ? last : undefined;
  });
  const [saving, setSaving] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  // Criminal/civil trial gate (spec §9.3): once the role choice is persisted,
  // a lawyer without a verified phone sees the OTP step before landing in the
  // workspace. Corporate never reaches this — it's a separate launch path.
  const [otpRole, setOtpRole] = useState<InAppRole | null>(null);

  const inAppCards = IN_APP_ROLE_ORDER.filter((r) => roles.has(r)).map((r) => ROLE_CARDS[r]);
  const showCorporate = roles.has('corporate');
  // Declared corporate but no admin grant yet (temporary pre-subscription
  // gate). Server-derived — never inferred from client-held role state.
  const corporatePending = profile?.corporate_access === 'pending';

  // Corporate is a SEPARATE app (corporate-web), not a main-app lens — selecting
  // it hands off to the Corporate Suite via SSO rather than setting a session lens.
  const launchCorporate = async () => {
    setLaunching(true);
    setLaunchError(null);
    // Claim the corporate trial before leaving for corporate-web. Same gap as
    // the corporate-only auto-redirect in App.tsx: this path hands off by SSO
    // and never mounts a TrialGate, so a lawyer choosing the Corporate card
    // got no trial — while the landing page sold them Firm and Firm Max.
    // Not awaited: the registrar swallows its own failures and the hand-off
    // must not wait on a billing call.
    void ensureTrialOnServer();
    try {
      await launchCorporateSuite();
      // Success navigates the browser away.
    } catch (e) {
      setLaunchError(e instanceof Error ? e.message : 'Could not open the Corporate Suite.');
      setLaunching(false);
    }
  };

  const confirm = async (card: RoleCard) => {
    setSaving(true);
    // Persist the active role first — the axios interceptor reads this key to
    // send X-Active-Role. Do this even if the lens write fails below. Also
    // remembers the choice against this sign-in (new-tab restore, Q28).
    // Session only: the same-sign-in stamp (LAST_ROLE) is written after the
    // TrialGate finishes, never before — otherwise a second tab restores the
    // role and skips the gate (review S8).
    enterRole(card.role);
    try {
      // Backward-compat: also set a representative session lens so existing
      // lens-based sidebar gating keeps working.
      await setActivePracticeArea(card.lens);
    } catch {
      // Non-fatal: the lens also persists to localStorage optimistically.
    } finally {
      setSaving(false);
      // Corporate never reaches confirm() (it has its own launch path via
      // launchCorporate), so every card here is criminal/civil — eligible
      // for the shared trial gate.
      setOtpRole(card.role);
    }
  };

  const renderRoleCard = (c: RoleCard, i: number) => {
    const Icon = c.icon;
    const isSelected = selected === c.role;
    return (
      <motion.button
        key={c.role}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 + i * 0.06 }}
        type="button"
        disabled={saving || launching}
        onClick={() => setSelected(c.role)}
        aria-pressed={isSelected}
        className={cn(
          'text-left rounded-xl border-2 p-5 transition-all disabled:opacity-60',
          isSelected
            ? `${c.selectedBorder} ${c.selectedBg} ring-2 ring-offset-1 ring-legal-green-400/30 shadow-elevation-2`
            : 'border-border-primary bg-surface-elevated hover:border-legal-green-400/50 hover:shadow-elevation-1'
        )}
      >
        <div className="flex items-start justify-between mb-3">
          <div className={cn('w-11 h-11 rounded-2xl flex items-center justify-center', c.iconBg)}>
            <Icon className="h-5 w-5" />
          </div>
          {isSelected && <Check className="h-5 w-5 text-legal-green-600" />}
        </div>
        <h3 className="font-semibold text-text-primary text-sm">{c.title}</h3>
        <p className="text-xs font-medium text-legal-green-600 dark:text-legal-green-300 mt-0.5">
          {c.subtitle}
        </p>
        <p className="text-xs text-text-tertiary mt-2 leading-snug">{c.description}</p>
      </motion.button>
    );
  };

  const selectedCard = selected ? ROLE_CARDS[selected] : undefined;

  // Additional screen inserted after role selection is persisted, for
  // criminal/civil lawyers without a verified phone yet. Skipping still
  // lands them in the workspace — they just don't get a provisional trial.
  if (otpRole) {
    return (
      <TrialGate
        role={otpRole}
        onDone={() => {
          rememberLastRole(otpRole, currentSignIn());
          onDone();
        }}
      />
    );
  }

  return (
    <div
      role="dialog"
      aria-label="Choose your workspace"
      className="fixed inset-0 z-[9998] bg-surface flex items-center justify-center p-4 overflow-y-auto"
    >
      <div className="max-w-5xl w-full">
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-legal-green-500 to-legal-green-700 flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Scale className="h-6 w-6 text-white" />
          </div>
          <h1 className="font-serif text-3xl font-bold text-text-primary">
            How are you practising today?
          </h1>
          <p className="mt-2 text-text-secondary">
            Pick the workspace for this session. Switch anytime from the header — and each
            matter always opens with its own practice area.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          {/* One card per in-app role the user is registered for. */}
          {inAppCards.map((c, i) => renderRoleCard(c, i))}

          {/* Corporate — a LAUNCH card (separate app via SSO), only when the
              user has the corporate role. */}
          {showCorporate && (
            <motion.button
              key="corporate"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 + inAppCards.length * 0.06 }}
              type="button"
              // Awaiting an admin grant: the corporate wall would refuse the
              // session anyway, so say so here rather than bounce them through
              // an SSO round-trip into a "no access" screen.
              disabled={saving || launching || corporatePending}
              aria-disabled={corporatePending || undefined}
              onClick={() => void launchCorporate()}
              className={cn(
                'text-left rounded-xl border-2 p-5 transition-all disabled:opacity-60',
                // QA Q43: a pending card must LOOK unavailable, not just ignore
                // clicks — dashed border, muted fill, dimmed and greyed.
                corporatePending
                  ? 'border-dashed border-border-primary bg-surface opacity-50 grayscale cursor-not-allowed'
                  : 'border-border-primary bg-surface-elevated hover:border-legal-green-400/50 hover:shadow-elevation-1'
              )}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="w-11 h-11 rounded-2xl flex items-center justify-center bg-legal-green-100 text-legal-green-700 dark:bg-legal-green-900/30 dark:text-legal-green-300">
                  {launching ? <Loader2 className="h-5 w-5 animate-spin" /> : <Building2 className="h-5 w-5" />}
                </div>
                {corporatePending ? (
                  <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border border-border-primary text-text-tertiary">
                    Awaiting approval
                  </span>
                ) : (
                  <ArrowUpRight className="h-4 w-4 text-text-tertiary" />
                )}
              </div>
              <h3 className="font-semibold text-text-primary text-sm">{ROLE_LABELS.corporate}</h3>
              <p className="text-xs font-medium text-legal-green-600 dark:text-legal-green-300 mt-0.5">
                M&amp;A · Compliance · Deals
              </p>
              <p className="text-xs text-text-tertiary mt-2 leading-snug">
                {corporatePending
                  ? 'Awaiting approval — we’re enabling corporate accounts one at a time during early release. You’ll get in as soon as it’s approved.'
                  : launching
                    ? 'Opening the Corporate Suite…'
                    : 'Opens the Corporate Legal Suite — deals, gates, compliance and the tail.'}
              </p>
            </motion.button>
          )}
        </div>

        {launchError && (
          <p className="text-center text-xs text-crimson-600 dark:text-crimson-400 mb-3">{launchError}</p>
        )}

        <div className="flex justify-center">
          <button
            type="button"
            disabled={selectedCard === undefined || saving || launching}
            onClick={() => selectedCard && void confirm(selectedCard)}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-legal-green-600 text-white font-medium hover:bg-legal-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default PracticeAreaChooser;
